/**
 * Fonte única de design tokens do E-Learning.
 * Cores, fontes e tamanhos centralizados — use useTheme() ou var(--...) no CSS.
 */

export const designTokens = {
  /** Cores semânticas */
  palette: {
    primary: {
      main: '#E0B600',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#203300',
      secondary: '#4A5565',
      white: '#FFFFFF',
      black: '#0A0A0A',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    border: {
      main: '#e5e7eb',
      input: '#D1D5DC',
      light: '#ccc',
    },
    error: {
      main: '#B91C1C',
    },
    input: {
      background: '#F3F3F5',
      placeholder: '#717182',
      text: '#717182',
    },
    /** Componente filtro (caixa única: busca + botão Filter) */
    filterBox: {
      border: '#E0E0E0',
      background: '#FFFFFF',
    },
    searchInFilter: {
      background: '#F3F3F5',
      placeholder: '#A0A0A0',
      icon: '#A0A0A0',
      white: '#FFFFFF',
    },
    button: {
      filterBg: '#FFFFFF',
      filterBorder: '#E0E0E0',
      filterText: '#0A0A0A',
      filterIcon: '#0A0A0A',
    },
    addUser: {
      background: '#E0B600',
      text: '#FFFFFF',
    },
    groups: {
      createButtonBackground: '#E0B600',
      createButtonText: '#FFFFFF',
      typeTagBackground: '#F3F3F5',
      typeTagText: '#1C2B39',
    },
    roles: {
      background: 'rgba(224, 182, 0, 0.1)',
      border: 'rgba(224, 182, 0, 0.2)',
      text: '#E0B600',
    },
    status: {
      background: '#F0FDF4',
      border: '#203300',
      text: '#203300',
    },
    questionBankGlobalSettings: {
      tabsBg: '#ECEEF1',
      infoBannerBg: 'rgba(32, 51, 0, 0.1)',
      infoBannerBorder: '#203300',
      infoIconBorder: '',
      globalAssessmentHeaderIconBoxBg: 'rgba(32, 51, 0, 0.1)',
      globalAssessmentHeaderIconBoxRadius: '10px',
      globalAssessmentHeaderIconBoxSize: 48,
      retryBannerBg: 'rgba(224, 182, 0, 0.08)',
      retryBannerBorder: 'rgba(224, 182, 0, 0.25)',
      retryIconBorder: 'rgba(224, 182, 0, 0.6)',
      maxAttemptsActiveBg: 'rgba(224, 182, 0, 0.06)',
    },
    /** Admin B2B — Gamification Engine (fundo do cartão = `palette.text.primary`; texto claro sobre o cartão) */
    adminGamification: {
      cardBorder: '#FFFFFF',
      cardTitleText: '#203300',
      validityDescriptionText: '#203300',
      inputRowGap: '1rem',
      /** Banner informativo: mesmo vocabulário que `questionBankGlobalSettings.retryBanner*` / `roles` */
      infoBannerBg: 'rgba(224, 182, 0, 0.15)',
      backgroundCard: '#FFFFFF',
      infoBannerBorder: 'rgba(224, 182, 0, 0.35)',
      infoBannerText: '#203300',
      infoBannerMarginTop: '1rem',
      infoBannerPadding: '1rem',
      infoBannerBorderRadius: '8px',
      errorAlertMarginBottom: '1rem',
      borderRadiusCard: '12px',
      paddingCard: 24,
      inputMinHeight: 48,
      /** Largura fixa (~3 dígitos + pequeno espaço); máx. 999; `border-box` */
      inputWidthPx: 68,
      inputPaddingX: '6px',
      inputBackground: 'rgba(255, 255, 255, 0.18)',
      inputBorder: '#203300',
      inputText: '#203300',
      inputBorderRadius: '8px',
      saveButtonBackground: '#E0B600',
      saveButtonText: '#FFFFFF',
      footerMarginTop: '2rem',
      saveButtonMinHeight: 42,
      saveButtonGap: '0.5rem',
      saveButtonPaddingY: '0.5rem',
      saveButtonPaddingX: '1.5rem',
      saveButtonBorderRadius: '6px',
      saveIconSize: 18,
    },
    /** Admin B2B — Leaderboard Management (KPIs, busca, tabela classificativa) */
    adminLeaderboard: {
      pageBackground: '#F8F9FA',
      titleText: '#1A1C1E',
      subtitleText: '#6C757D',
      cardBackground: '#FFFFFF',
      cardBorder: '#E5E7EB',
      cardBorderRadius: '12px',
      cardPadding: '16px',
      statCardsGap: '20px',
      statLabelText: '#4A5565',
      statValueDefault: '#203300',
      statValuePoints: '#203300',
      statValueEngagement: '#203300',
      statIconBoxSize: 48,
      statIconBoxRadius: '10px',
      statIconBoxBgDefault: '#F3F4F6',
      statIconBoxBgOrange: 'rgba(234, 88, 12, 0.12)',
      statIconBoxBgGreen: 'rgba(0, 130, 54, 0.12)',
      statIconImageSize: 28,
      sectionGapAfterTitle: '24px',
      sectionGapAfterStats: '24px',
      /** Cartão branco à volta da busca (borda fina, cantos ~24px) */
      searchBarOuterBackground: '#FFFFFF',
      searchBarOuterBorder: '#E5E7EB',
      searchBarOuterBorderRadius: '14px',
      searchBarOuterPadding: '12px',
      /** Pílula interna cinza-claro, sem borda */
      searchBarBackground: '#F1F3F5',
      searchBarBorderRadius: '10px',
      searchBarMinHeight: 44,
      searchBarPaddingX: '16px',
      searchIconSize: 20,
      searchInputText: '#1A1C1E',
      searchPlaceholderText: '#6B7280',
      tableCardPadding: '0',
      tableHeaderText: '#1C2B39',
      tableHeaderBorderBottom: '#E5E7EB',
      tableRowBorderBottom: '#E5E7EB',
      tableRowHeightMin: 72,
      tableCellPaddingY: '16px',
      tableCellPaddingX: '16px',
      rowBackground: '#E86C1F0D',
      rankTextMuted: '#6C757D',
      salesRepNameText: '#1A1C1E',
      salesRepEmailText: '#6C757D',
      avatarSize: 40,
      avatarBackground: '#203300',
      avatarText: '#FFFFFF',
      pointsIconSize: 18,
      lastActiveText: '#6C757D',
      /** Círculo 40×40 à volta do ícone / label nos lugares 1–3 */
      rankMedalCircleSize: 40,
      /** Ícone PNG dentro do círculo (1.º lugar e, se aplicável, 2.º/3.º) */
      rankMedalIconSize: 24,
      rankTop1MedalGradient:
        'linear-gradient(135deg, #FDC700 0%, #D08700 100%)',
      rankTop2MedalGradient:
        'linear-gradient(135deg, #D1D5DC 0%, #6A7282 100%)',
      rankTop3MedalGradient:
        'linear-gradient(135deg, #FF8904 0%, #F54900 100%)',
    },
    /** Admin B2B — Dashboard (KPIs Analytics Overview) */
    adminDashboard: {
      analyticsKpi: {
        cardBorderRadius: '12px',
        cardPadding: '16px',
        rowGap: '4px',
        /** Espaço entre cartões na grelha responsiva */
        cardGridGap: '20px',
        trendArrowSize: 14,
        /** Degradê vertical: topo → branco */
        blue: {
          gradient: 'linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)',
          border: '#BEDBFF',
        },
        purple: {
          gradient: 'linear-gradient(180deg, #FAF5FF 0%, #FFFFFF 100%)',
          border: '#E9D4FF',
        },
        green: {
          gradient: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)',
          border: '#B9F8CF',
        },
        yellow: {
          gradient: 'linear-gradient(180deg, #FEFCE8 0%, #FFFFFF 100%)',
          border: '#FFF085',
        },
        trendPositive: '#203300',
        trendNegative: '#B91C1C',
        trendMuted: '#6C757D',
      },
      /** Gráficos 2×2 (Engagement, Course performance, Assessment, Leaderboard preview) */
      charts: {
        cardBackground: '#FFFFFF',
        cardBorder: '#E5E7EB',
        cardBorderRadius: '12px',
        cardPadding: '16px',
        cardMinHeight: 320,
        /** Altura útil do SVG Recharts (px) */
        chartHeightPx: 200,
        /** Engagement — linha (mock: mais alto para eixo 0–180) */
        engagementChartHeightPx: 220,
        /** Grelha da página: gap entre cartões de gráfico */
        gridGap: '20px',
        /** Duas colunas: 2 gráficos por linha; última linha = assessment + lista leaderboard */
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        /** Colunas mínimas (layouts alternativos / legado) */
        gridMinColWidth: '280px',
        pageSectionMarginTop: '24px',
        titleColor: '#1F2937',
        subtitleColor: '#6B7280',
        iconAccent: '#EAB308',
        /** Linhas de grelha / eixos */
        gridStroke: '#E5E7EB',
        axisTick: '#6B7280',
        /** Séries — engagement (linha: total navy, ativos dourado) */
        lineTotalUsers: '#203300',
        lineActiveUsers: '#E0B600',
        lineDotFill: '#FFFFFF',
        /** Course performance — barras agrupadas (mock: verde mar / âmbar) */
        barCompletion: '#203300',
        barInProgress: '#E0B600',
        /** Assessment — fatias / legendas */
        slicePassed: '#203300',
        sliceFailed: '#EF4444',
        slicePending: '#E0B600',
        statLabelMuted: '#6B7280',
        /** Leaderboard preview */
        rowBackground: '#F3F4F6',
        rowBorderRadius: '10px',
        /** Padding interno de cada linha (cartão) */
        rowPadding: '12px',
        /** Espaço entre rank, nome e pontos dentro da linha */
        rowItemGap: '12px',
        rowGap: '8px',
        rankCircleTopSize: 28,
        rankCircleDefaultSize: 28,
        rankCircleTopBackground: '#E0B600',
        rankCircleTopText: '#FFFFFF',
        rankCircleDefaultBackground: '#E5E7EB',
        rankCircleDefaultText: '#6B7280',
        pointsAccent: '#E0B600',
        cardShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      },
    },
    gradient: {
      page: 'linear-gradient(135deg, #203300 0%, #203300 100%)',
    },
    modalDynamic: {
      error: '#B91C1C',
      autocomplete: {
        background: 'rgba(217, 186, 0, 0.1)',
        border: 'rgba(217, 186, 0, 0.2)',
        text: '#E0B600',
      },
    },
    /** Dashboard do aluno (B2B): banner XP, grupo, ranking */
    studentDashboard: {
      xpBanner: {
        gradient:
          'linear-gradient(90deg, #203300 0%, #203300 52%, #203300 100%)',
        /** Área total do cartão (px) */
        width: 906,
        height: 130,
        /** "Total XP" e sufixo "XP" — branco ~70% opacidade */
        labelMuted: '#FFFFFF',
        unitMuted: '#FFFFFF',
        /** Valor numérico do XP — alinhado a primary.main */
        valueColor: '#E0B600',
        /** Círculo ao redor do ícone de troféu (px), sem borda */
        trophyBoxSize: 80,
        /** Largura/altura do PNG do troféu (px) */
        trophyIconImageSize: 40,
        /** Alinhado ao primary com opacidade */
        trophyBoxBackground: 'rgba(217, 186, 0, 0.16)',
        /** Linha de base do valor numérico (forte contraste vertical) */
        valueLineHeight: 1,
      },
      /** Pill “Your group” — texto #203300, fundo rgba(32, 51, 0, 0.1) (alinhado a createCourse.sidebar progress) */
      groupPill: {
        background: 'rgba(32, 51, 0, 0.1)',
        border: '#203300',
        text: '#203300',
      },
      ranking: {
        /** Cabeçalho do painel = mesma base do app (sidebar) */
        headerBackground: '#203300',
        /** Linhas fora do top 3 */
        rowBackground: '#F3F4F6',
        /** Posições 1–3 — wash primary (harmoniza com estrela dourada) */
        topThreeRowBackground: 'rgba(217, 186, 0, 0.14)',
        avatarBackground: '#203300',
        avatarBorder: '#E0B600',
        currentUserBorder: '#203300',
        headerText: '#FFFFFF',
        avatarText: '#FFFFFF',
        /**
         * Badge circular da estrela no top 3.
         * Ícone: `iconStudentStar` → `assets/student/icon-star.png` (estrela dourada em fundo claro).
         */
        starBadgeBackground: '#E0B600',
        starBadgeShadow: '0 1px 4px rgba(28, 43, 57, 0.18)',
        /** Deslocamento do badge da estrela (px, valores negativos no componente) */
        starBadgeOffset: 3,
        nameText: '#1C2B39',
        scoreText: '#6A7282',
        scoreTextMuted: '#99A1AF',
        /** Linha “eu” — verde claro institucional (`status.background`), sem tom azul */
        currentUserBackground: '#F0FDF4',
        medalIconBackground: '#E0B600',
      },
      courseCarousel: {
        countBadgeText: '#030213',
        countBadgeBackground: '#ECEEF2',
        /** Badge de contagem na secção “Watch again” */
        watchAgainCountBadge: {
          text: '#008236',
          background: '#DCFCE7',
          border: 'none' as const,
        },
        /** Tom primário (dourado) com transparência — alinha aos botões do aluno e dá mais destaque ao hover. */
        arrowButtonOverlayBackground: 'rgba(217, 186, 0, 0.32)',
        /** Botões anterior/seguinte do carrossel (px) */
        navButtonSize: 40,
        navIconSize: 18,
        /** Ícone 20×20 ao lado do título da secção */
        sectionTitleIconSize: 20,
        /** Transição e opacidades dos botões de navegação (CSS) */
        carouselArrowTransitionMs: 120,
        carouselArrowOpacityDefault: 0.45,
        carouselArrowOpacityHover: 0.85,
        carouselArrowOpacityDisabled: 0.15,
        carouselArrowHoverTranslateY: -2,
      },
      aside: {
        boxShadow: '0 4px 18px rgba(0, 0, 0, 0.06)',
      },
    },
    /** Course Player do aluno (header, conteúdo e sidebar). */
    studentCoursePlayer: {
      header: {
        height: 70,
      },
      quiz: {
        successText: '#008236',
        successBackground: '#DCFCE7',
        failText: '#F54900',
        failBackground: '#FFEDD4',
        mutedText: '#6A7282',
        errorText: '#B91C1C',
        xpText: '#E0B600',
        noteBackground: 'rgba(217, 186, 0, 0.18)',
        questionCounterText: '#1C2B39',
        questionCounterBackground: '#FFFFFF',
      },
      fileBadge: {
        pdfBackground: '#E53935',
        xlsxBackground: '#2E7D32',
        docxBackground: '#1565C0',
        text: '#FFFFFF',
      },
      content: {
        videoUnavailableBackground: '#0F1115',
      },
      sidebar: {
        activeRowBackground: 'rgba(217, 186, 0, 0.12)',
        progressTrackBackground: '#E8EAF0',
        moduleHeaderBackground: '#F9FAFB',
        xpBadgeText: '#806A00',
        xpBadgeBackground: 'rgba(217, 186, 0, 0.2)',
      },
    },
    /** Create Course (sidebar progress, basic info card) */
    createCourse: {
      sidebar: {
        stepCompletedBg: '#B9F8CF',
        stepCompletedBorder: '#F0FDF4',
        stepCompletedCircle: '#00C950',
        stepCompletedTitle: '#6A7282',
        stepCompletedSubtitle: '#008236',
        stepActiveBg: 'rgba(217, 186, 0, 0.12)',
        progressBoxBg: '#EFF6FF',
        progressLabelText: '#1E3A5F',
        progressValueText: '#1447E6',
        progressBarBg: '#BEDBFF',
        progressBarFill: '#155DFC',
        /** Largura do card da sidebar (px); altura da barra de progresso (px) */
        cardWidth: 291,
        progressBarHeight: 8,
        stepLabelMarginBottom: 2,
      },
      basicInfo: {
        /** Verde institucional (#203300) a 10% — caixa do ícone do card */
        iconBoxBg: 'rgba(32, 51, 0, 0.1)',
        uploadZoneDragBg: 'rgba(217, 186, 0, 0.05)',
        /** Padding-left do subtítulo para alinhar ao conteúdo (52px) */
        sectionSubtitlePaddingLeft: '3.25rem',
      },
      /** Step 2 – Curriculum (ícone do card) */
      curriculum: {
        iconBoxBg: 'rgba(32, 51, 0, 0.1)',
      },
      /** Step 3 – Targeting (ícone do card, counters, search) */
      targeting: {
        iconBoxBg: 'rgba(32, 51, 0, 0.1)',
        counterBlueBg: '#EFF6FF',
        counterBlueBorder: 'rgba(59, 130, 246, 0.2)',
        counterPurpleBg: '#FAF5FF',
        counterPurpleBorder: 'rgba(126, 34, 206, 0.2)',
        searchResultsBg: '#F9FAFB',
      },
      /** Modais (overlay e sombra) */
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      modalShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      /** Quiz modal – blocos manual/total/final exam */
      quizModal: {
        manualBlockBg: '#F5F8FF',
        manualBlockBorder: '#E2E8F0',
        totalBlockBg: '#2C3E50',
        totalBlockBorder: '#2C3E50',
        finalExamBg: '#FEF2F2',
        finalExamBorder: '#FECACA',
        finalExamText: '#B91C1C',
        finalExamSubtitle: '#DC2626',
        toggleThumbShadow: '0 1px 3px rgba(0,0,0,0.2)',
      },
      /** Dropdown Add Lesson (sombra do menu) */
      dropdownBoxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      /** Tamanhos (px) – ícones, checkbox, search */
      iconBoxSize: 40,
      iconSize: 24,
      iconSizeSmall: 16,
      addModuleIconSize: 18,
      checkboxSize: 16,
      dragHandleSize: 20,
      chevronSize: 14,
      deleteBtnSize: 32,
      xpInputWidth: 44,
      searchIconOffset: 20,
      searchResultsMaxHeight: 280,
      /** Create Question form – dropdown e lista de cursos vinculados */
      selectDropdownPaddingRight: 36,
      linkedCoursesListMaxHeight: 200,
    },
  },

  /** Fontes e tipografia */
  typography: {
    fontFamily:
      "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontFamilySerif: 'Georgia, serif',
    fontFamilyMono:
      "ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, monospace",
    fontSize: {
      xxs: '0.625rem', // 10px
      xs: '0.75rem', // 12px
      sm: '0.8125rem', // 13px
      base: '0.875rem', // 14px
      lead: '0.9rem', // texto lead
      md: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.75rem', // 28px
      /** KPI / destaque numérico (ex.: total XP no dashboard do aluno) */
      '5xl': '3rem', // 48px
      /** Admin Dashboard — valor principal dos cartões Analytics (30px) */
      analyticsKpiValue: '1.875rem',
      /** Título dos cartões de gráfico (Admin Dashboard) */
      chartCardTitle: '1rem',
      chartCardSubtitle: '0.8125rem',
      /** Percentuais grandes na legenda do pie (Assessment) */
      chartStatPercent: '1.25rem',
    },
    fontWeight: {
      thin: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.625,
    },
  },

  /** Espaçamento (rem) */
  spacing: {
    xxs: '0.125rem', // 2px
    xs: '0.25rem', // 4px
    xs2: '0.375rem', // 6px
    sm: '0.5rem', // 8px
    sm2: '0.75rem', // 12px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    '2xl': '2.5rem', // 40px
    '3xl': '3rem', // 48px
    '4xl': '3.5rem', // 56px
    inputY: '0.625rem',
    inputX: '0.75rem',
    buttonSmallY: '0.375rem',
    buttonSmallX: '1rem',
    buttonMediumX: '1.5rem',
    buttonLargeY: '0.75rem',
    buttonLargeX: '2rem',
  },

  /** Layout (larguras máximas etc.) */
  layout: {
    formMaxWidth: '400px',
    /** Largura máxima do conteúdo de páginas (ex.: criar pergunta) */
    contentMaxWidth: '800px',
    /** Tamanho base do card (px) */
    cardWidth: 448,
    cardHeight: 472,
    logoWidth: 138,
    logoHeight: 59,
    /** Sidebar e cabeçalho (pós-login) */
    sidebarWidth: '240px',
    /**
     * Altura padrão do header (layout sem sidebar ou sem necessidade de alinhamento com o logo).
     * Mantida para não afetar fluxos onde `hideSidebar=true`.
     */
    headerHeight: '56px',
    /**
     * Altura do header quando a sidebar está visível: alinha com a "linha" abaixo do logo da sidebar.
     * Considera: paddingTop (md=1rem) + paddingBottom (lg=1.5rem) + logo (maxHeight~40px) + borderBottom (1px).
     */
    headerHeightWithSidebar: 'calc(1rem + 1.5rem + 40px + 1px)',
    /** Question Bank – tabela e paginação */
    questionBankTable: {
      actionColumnWidth: 100,
      difficultyColumnWidth: 100,
      usageColumnWidth: 80,
      questionColumnMaxWidth: 280,
      linkedCoursesMaxWidth: 200,
      tableCardShadow: '0 1px 3px rgba(0,0,0,0.08)',
      paginationButtonMinWidth: 32,
    },
    /** Question Bank – filtro (caixa busca + dropdowns + botão) */
    questionBankFilter: {
      boxFlexBasis: 280,
      boxMinWidth: 200,
      boxMaxWidth: 720,
      searchMinWidth: 90,
      searchMinHeight: 40,
      filterButtonWidth: 140,
      filterButtonHeight: 40,
      addButtonMinWidth: 180,
      addButtonHeight: 42,
    },
    /** Admin — Leaderboard (larguras mínimas de colunas) */
    adminLeaderboard: {
      colRankMinWidth: 88,
      colSalesRepMinWidth: 220,
      colPointsMinWidth: 140,
      colCoursesMinWidth: 140,
      colLastActiveMinWidth: 128,
      statCardMinWidth: 200,
    },
    /** Dashboard do aluno — larguras e slots */
    studentDashboard: {
      contentMaxWidth: 1320,
      mainFlexBasis: '740px',
      /** Centrar shell horizontalmente */
      shellHorizontalMargin: '0 auto',
      /** Hint à direita dos chips de grupo — `flex: 1 1 …` */
      groupHintFlexBasis: '200px',
      /**
       * Acima desta posição no ranking mostra-se “…” antes do utilizador atual.
       * (Lógica em `StudentRankingPanel`.)
       */
      rankingGapAfterRank: 5,
      /** Largura mínima do aside: linha do ranking (252px) + padding lateral (md × 2) */
      asideWidth: 284,
      courseCardSlotMinWidth: 320,
      /** Cada linha do ranking (px); border-radius 10px = shape.borderRadius.md */
      rankingRowWidth: 252,
      rankingRowHeight: 64,
      rankingPositionMinWidth: 18,
      rankingAvatarSize: 40,
      rankingMedalIconSize: 40,
      /** Badge circular da estrela no avatar (top 3), px */
      rankingStarBadgeSize: 18,
      /** Largura/altura do PNG da estrela dentro do badge, px */
      rankingStarIconSize: 11,
      rankingHeaderIconSize: 20,
    },
  },

  /** Cores do layout app (sidebar + header) */
  appLayout: {
    sidebarBg: '#203300',
    headerBg: '#203300',
    menuItemActiveBg: '#E0B600',
    menuItemActiveText: '#FFFFFF',
    menuItemText: '#D1D5DC',
    headerTitleText: '#FFFFFF',
    copyrightText: '#FFFFFF',
  },

  /** Bordas e raios */
  shape: {
    borderRadius: {
      xs: '4px',
      sm: '6px',
      sm2: '8px',
      sm3: '12px',
      md: '10px',
      lg: '16px',
      full: '9999px',
    },
    borderWidth: {
      thin: '1px',
      medium: '2px',
    },
  },
} as const;

export type Theme = typeof designTokens;
